import type { ListBuilderColumn } from '../../types';
import {
	createCategoryColumnConditional,
	createCategoryColumnStatic,
	createImageColumn,
	createItemDetailsColumnStatic,
	createItemDetailsColumnTheme,
	createLastInvoiceDataColumnStatic,
	createLastInvoiceDataColumnTheme,
	createPricingColumnStatic,
	createPricingColumnTheme,
	createSalesDataColumnStatic,
	createSalesDataColumnTheme,
	createSizeColumnStatic,
	createSizeColumnTheme,
	createStockColumnStatic,
	createStockColumnTheme,
} from '../client-shop/columns';

/**
 * The quote shop shares the client shop's item-detail columns (PD-14379).
 * This file used to carry a full fork of those column definitions, which
 * silently drifted as the shop gained promo badges, attribute icons, the
 * price-change/price-contract indicators, conditional Item # display, and
 * dynamic month headers. Reusing the client-shop factories keeps the two
 * surfaces identical by construction — only the quote-specific quantity and
 * price input columns are defined here.
 */

/**
 * Quantity input column - custom component for quote shop
 */
export const createQuantityColumn = (): ListBuilderColumn => ({
	id: 'quantity',
	width: 150,
	fields: [
		{
			id: 'shop_quantity',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: '_id',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'custom',
			customComponent: {
				id: 'quote-shop-quantity-input',
			},
		},
	],
	headers: [
		{
			id: 'quantity_header',
			text: 'Qty',
		},
	],
	sortable: false,
	alignment: 'center',
	verticalAlignment: 'middle',
});

/**
 * Price input column - custom component for quote shop
 */
export const createPriceInputColumn = (): ListBuilderColumn => ({
	id: 'price',
	width: 100,
	fields: [
		{
			id: 'shop_price',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: '_id',
					objectTable: 'item',
				},
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'custom',
			customComponent: {
				id: 'quote-shop-price-input',
			},
		},
	],
	headers: [
		{
			id: 'price_header',
			text: 'Price',
		},
	],
	sortable: false,
	alignment: 'center',
	verticalAlignment: 'middle',
});

/**
 * Standard columns for quote_shop_default list — the client shop's default
 * item columns with the quote's quantity/price inputs in place of the shop
 * actions column.
 */
export const createQuoteShopDefaultColumns = (): Array<ListBuilderColumn> => [
	createImageColumn(),
	createItemDetailsColumnTheme(),
	createCategoryColumnConditional(),
	createSizeColumnTheme(),
	createPricingColumnTheme(),
	createStockColumnTheme(),
	createLastInvoiceDataColumnTheme(),
	createSalesDataColumnTheme(),
	createQuantityColumn(),
	createPriceInputColumn(),
];

/**
 * Standard columns for secondary quote shop lists — mirrors the client
 * shop's standard (static-color) column set with the quote inputs appended.
 */
export const createQuoteShopStandardColumns = (): Array<ListBuilderColumn> => [
	createImageColumn(),
	createItemDetailsColumnStatic(),
	createCategoryColumnStatic(),
	createSizeColumnStatic(),
	createPricingColumnStatic(),
	createStockColumnStatic(),
	createLastInvoiceDataColumnStatic(),
	createSalesDataColumnStatic(),
	createQuantityColumn(),
	createPriceInputColumn(),
];
